import { create } from 'zustand';

import { toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';

import { fetchFinishedMatches, fetchOpenMatches } from '../api/matches.api';

import type { Prediction } from '@/shared/api';

import type { Match } from './types';

interface MatchesState {
  items: Match[];
  isLoading: boolean;
  error: string | null;
  loadedAt: number | null;

  /** Вкладка «Завершённые» — сыгранные матчи сегодняшнего пула. */
  finished: Match[];
  isLoadingFinished: boolean;
  finishedError: string | null;
  finishedLoadedAt: number | null;

  load: (options?: { force?: boolean }) => Promise<void>;
  loadFinished: (options?: { force?: boolean }) => Promise<void>;
  /**
   * Отмечает тип Прогноза сделанным, не перезапрашивая ленту.
   *
   * `alreadyPredicted` считает сервер, но между подтверждением и следующей
   * загрузкой карточка осталась бы устаревшей, и игрок увидел бы кнопку,
   * на которую сервер уже ответит 409.
   */
  applyPrediction: (matchId: string, prediction: Prediction) => void;
  reset: () => void;
}

export const useMatchesStore = create<MatchesState>()((set, get) => ({
  items: [],
  isLoading: false,
  error: null,
  loadedAt: null,

  finished: [],
  isLoadingFinished: false,
  finishedError: null,
  finishedLoadedAt: null,

  load: singleFlight(async (options?: { force?: boolean }): Promise<void> => {
    if (!options?.force && isFresh(get().loadedAt)) return;

    set({ isLoading: true, error: null });
    try {
      set({ items: await fetchOpenMatches(), loadedAt: Date.now() });
    } catch (error) {
      set({ error: toPlayerMessage(error) });
    } finally {
      set({ isLoading: false });
    }
  }),

  loadFinished: singleFlight(
    async (options?: { force?: boolean }): Promise<void> => {
      if (!options?.force && isFresh(get().finishedLoadedAt)) return;

      set({ isLoadingFinished: true, finishedError: null });
      try {
        set({
          finished: await fetchFinishedMatches(),
          finishedLoadedAt: Date.now(),
        });
      } catch (error) {
        set({ finishedError: toPlayerMessage(error) });
      } finally {
        set({ isLoadingFinished: false });
      }
    },
  ),

  applyPrediction: (matchId, prediction) => {
    set({
      items: get().items.map((match) =>
        match.id === matchId
          ? {
              ...match,
              myPredictions: [...match.myPredictions, prediction],
              availableTypes: match.availableTypes.map((available) =>
                available.type === prediction.type
                  ? { ...available, alreadyPredicted: true }
                  : available,
              ),
            }
          : match,
      ),
    });
  },

  reset: () => {
    set({ items: [], error: null, loadedAt: null });
    set({ finished: [], finishedError: null, finishedLoadedAt: null });
  },
}));
